import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { publicApi } from '../lib/api';
import { usePlayer } from '../context/PlayerContext';
import { Search as SearchIcon, Music2 } from 'lucide-react';

const SearchPage = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ songs: [], playlists: [] });
  const [loading, setLoading] = useState(false);
  const { playSong } = usePlayer();

  useEffect(() => {
    if (!q.trim()) {
      setResults({ songs: [], playlists: [] });
      return;
    }
    const t = setTimeout(() => {
      setLoading(true);
      publicApi.search(q).then((data) => {
        setResults(data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen pb-32" data-testid="search-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="relative fade-in-up">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#BFA5A5]" />
          <input
            data-testid="search-input"
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search songs, artists, playlists..."
            className="w-full h-14 pl-12 pr-4 rounded-full bg-[#220A0A] border border-[#3D1A1A] text-[#FFF3E0] placeholder-[#BFA5A5]/60 focus:outline-none focus:border-[#FFB800] font-body"
          />
        </div>

        {loading && <div className="mt-8 text-[#BFA5A5] text-sm">Searching...</div>}

        {!loading && q && results.songs.length === 0 && results.playlists.length === 0 && (
          <div className="mt-16 text-center text-[#BFA5A5]">
            <Music2 className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p>No results for "{q}".</p>
            <p className="text-xs mt-2">Only curated content is searchable.</p>
          </div>
        )}

        {results.playlists.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-xl text-[#FFF3E0] mb-3">Playlists</h2>
            <div className="grid grid-cols-2 gap-3">
              {results.playlists.map((pl) => (
                <Link
                  key={pl.id}
                  to={`/playlist/${pl.slug}`}
                  data-testid={`search-playlist-${pl.slug}`}
                  className="flex items-center gap-3 p-2 rounded-xl border border-[#3D1A1A] hover:border-[#FFB800] transition-colors"
                >
                  <img src={pl.cover_image} alt="" className="w-14 h-14 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#FFF3E0] truncate">{pl.name}</div>
                    <div className="text-xs text-[#BFA5A5] truncate">{pl.category_type}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {results.songs.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-xl text-[#FFF3E0] mb-3">Songs</h2>
            <ul>
              {results.songs.map((song, i) => (
                <li key={song.id}>
                  <button
                    data-testid={`search-song-${i}`}
                    onClick={() => playSong(song)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#220A0A] transition-colors text-left"
                  >
                    <img src={song.thumbnail_url} alt="" className="w-12 h-12 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm text-[#FFF3E0]">{song.title}</div>
                      <div className="text-xs text-[#BFA5A5] truncate">{song.artist}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
};

export default SearchPage;
